import { useState, useCallback, useRef } from 'react';
import { useFlightCasesService } from './useFlightChangeCases';
import { useAuthStore } from '../../stores/authStore';
import { useTranslation } from '../i18n/useTranslation';

export interface ConflictError {
  awb: string;
}

export interface GenericError {
  message: string;
}

export interface AssignedAnalyst {
  id: number;
  name: string;
  email: string;
}

export interface UseAssignmentOptions {
  onSuccess?: () => void | Promise<void>;
  onError?: () => void | Promise<void>;
  onOptimisticUpdate?: (flightChangeId: number, analyst: AssignedAnalyst) => void;
}

export interface UseAssignmentReturn {
  assigningCaseId: number | null;
  conflictError: ConflictError | null;
  genericError: GenericError | null;
  handleAssign: (flightChangeId: number, awb: string) => Promise<boolean>;
  clearError: () => void;
  isAssigning: (id: number) => boolean;
}

export const useAssignment = (options?: UseAssignmentOptions): UseAssignmentReturn => {
  const [assigningCaseId, setAssigningCaseId] = useState<number | null>(null);
  const [conflictError, setConflictError] = useState<ConflictError | null>(null);
  const [genericError, setGenericError] = useState<GenericError | null>(null);
  const flightCasesService = useFlightCasesService();
  const userProfile = useAuthStore((state) => state.userProfile);
  const account = useAuthStore((state) => state.account);
  const { t } = useTranslation();
  
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const handleAssign = useCallback(async (flightChangeId: number, awb: string): Promise<boolean> => {
    setAssigningCaseId(flightChangeId);
    setConflictError(null);
    setGenericError(null);
    
    try {
      
      await flightCasesService.assignFlightCase(flightChangeId.toString());
      
      const userName = userProfile?.name || account?.name || '';
      const userEmail = userProfile?.email || account?.username || '';
      const userId = userProfile?.id ? parseInt(userProfile.id, 10) || 0 : 0;
      
      if (userName && optionsRef.current?.onOptimisticUpdate) {
        const analyst: AssignedAnalyst = {
          id: userId,
          name: userName,
          email: userEmail,
        };
        optionsRef.current.onOptimisticUpdate(flightChangeId, analyst);
      } else {
        await optionsRef.current?.onSuccess?.();
      }
      
      return true;
    } catch (err) {
      console.error('Error assigning flight case:', err);
      if (err && typeof err === 'object' && 'status' in err && err.status === 409) {
        setConflictError({ awb });
      } else {
        setGenericError({ message: t('flightChangesCases.assignError.message') });
      }
      await optionsRef.current?.onError?.();
      return false;
    } finally {
      setAssigningCaseId(null);
    }

  }, [flightCasesService, userProfile, account, t]);

  const clearError = useCallback(() => {
    setConflictError(null);
    setGenericError(null);
  }, []);

  const isAssigning = useCallback((id: number) => {
    return assigningCaseId === id;
  }, [assigningCaseId]);

  return {
    assigningCaseId,
    conflictError,
    genericError,
    handleAssign,
    clearError,
    isAssigning,
  };
};
